import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { type InternalLoginConfig, api } from '@/lib/api'
import { getDashboardDefaultPath } from '@/lib/dashboard-shell'
import { UserCog } from 'lucide-react'
import { useEffect, useState } from 'react'

export default function LoginAdminRoute() {
  const [config, setConfig] = useState<InternalLoginConfig | null>(null)
  const [account, setAccount] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    let mounted = true
    api.internalLoginConfig().then((nextConfig) => {
      if (mounted) setConfig(nextConfig)
    })
    return () => {
      mounted = false
    }
  }, [])

  async function submit() {
    setIsSubmitting(true)
    setError('')
    try {
      const sessionUser = await api.internalLogin({
        kind: 'admin',
        account,
        password,
      })
      localStorage.setItem('miaoform_demo_user', JSON.stringify(sessionUser))
      window.location.href = getDashboardDefaultPath(sessionUser)
    } catch {
      setError('帳號或密碼不正確，或內部一般管理員登入未啟用。')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!config) {
    return <InternalLoginStatus title="一般管理員登入" message="檢查內部登入設定" />
  }

  if (!config.enabled || !config.loginAdmin) {
    return (
      <InternalLoginStatus
        title="一般管理員登入"
        message="內部一般管理員登入未設定"
        description="此路由保留給直接進入的內部帳密登入；請確認 INTERNAL_ADMIN_* encrypted secrets 已設定。一般使用者請回登入頁使用 Google Login。"
      />
    )
  }

  return (
    <main className="grid min-h-screen place-items-center bg-slate-950 p-6">
      <section className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="grid size-11 place-items-center rounded-lg bg-indigo-600 text-white">
            <UserCog className="size-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-950">一般管理員登入</h1>
            <p className="mt-1 text-sm font-medium text-slate-700">內部帳密一般管理員入口</p>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              一般管理員帳號與密碼由 server-side env 驗證；登入後會對應到管理員清單中的角色與狀態。
            </p>
          </div>
        </div>
        <div className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="admin-account">管理員帳號</Label>
            <Input
              id="admin-account"
              type="text"
              value={account}
              onChange={(event) => setAccount(event.currentTarget.value)}
              autoComplete="username"
              placeholder="輸入內部一般管理員帳號"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password">密碼</Label>
            <Input
              id="admin-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.currentTarget.value)}
              autoComplete="current-password"
              placeholder="輸入內部一般管理員密碼"
            />
          </div>
          {error ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          ) : null}
          <Button type="button" className="w-full" onClick={submit} disabled={isSubmitting}>
            登入一般管理員
          </Button>
        </div>
      </section>
    </main>
  )
}

function InternalLoginStatus({
  title,
  message,
  description,
}: {
  title: string
  message: string
  description?: string
}) {
  function backToLogin() {
    window.location.href = '/login'
  }

  return (
    <main className="grid min-h-screen place-items-center bg-slate-950 p-6">
      <section className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-slate-950">{title}</h1>
        <p className="mt-4 text-base font-medium text-slate-800">{message}</p>
        {description ? (
          <p className="mt-2 text-sm leading-6 text-slate-500">{description}</p>
        ) : null}
        <Button type="button" className="mt-6" onClick={backToLogin}>
          回登入頁
        </Button>
      </section>
    </main>
  )
}
